/**
 * Comment cooldown (owner decision, 2026-11-01): four comments per member per
 * conversation in a rolling 60 seconds, on match, offer and purchase comments.
 *
 * This is a CLIENT courtesy, not a security control: it counts the comments
 * already stored in the conversation (so a failed send never counts), and it
 * cannot stop a direct write, a modified client or an older installed app.
 * A server-side limit was explicitly deferred. Listing Q&A is out of scope.
 *
 * Pure, so the boundary, expiry and per-sender behaviour are unit-tested
 * (src/utils/__tests__/commentCooldown.test.js); the hook in
 * src/hooks/useCommentCooldown.js adds the one-second tick for the countdown.
 */
export const COMMENT_COOLDOWN_LIMIT = 4;
export const COMMENT_COOLDOWN_WINDOW_MS = 60 * 1000;

function toMs(value) {
  if (value == null) return NaN;
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value.seconds === 'number') return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? NaN : d.getTime();
}

/**
 * @param entries  stored conversation entries ({ from, at } for match
 *                 threads, { from, timestamp, type } for offer history)
 * @param uid      the member about to send
 * @param now      ms since epoch
 * @param isComment optional filter (offer history mixes offers, responses and
 *                 notes; only notes are comments)
 * @returns { allowed, remainingMs, count }  count = this member's comments
 *          inside the window
 */
export function commentCooldownState(entries, uid, now = Date.now(), { isComment = null, limit = COMMENT_COOLDOWN_LIMIT, windowMs = COMMENT_COOLDOWN_WINDOW_MS } = {}) {
  if (!uid || !Array.isArray(entries)) return { allowed: true, remainingMs: 0, count: 0 };
  const times = entries
    .filter((e) => e && e.from === uid && (!isComment || isComment(e)))
    .map((e) => toMs(e.at !== undefined ? e.at : e.timestamp))
    .filter((t) => Number.isFinite(t) && now - t < windowMs && now - t >= 0)
    .sort((a, b) => a - b);
  if (times.length < limit) return { allowed: true, remainingMs: 0, count: times.length };
  // The block lifts when the oldest of the last `limit` comments leaves the window.
  const gate = times[times.length - limit];
  return { allowed: false, remainingMs: Math.max(0, gate + windowMs - now), count: times.length };
}

export const isOfferComment = (entry) => !!entry && entry.type === 'note';

export function cooldownMessage(remainingMs) {
  const s = Math.max(1, Math.ceil(remainingMs / 1000));
  return `You've sent ${COMMENT_COOLDOWN_LIMIT} comments in the last minute. You can comment again in ${s} second${s === 1 ? '' : 's'}.`;
}
